import {
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { LoginFlow, Session, UpdateLoginFlowBody } from '@ory/client';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxFormFieldComponent } from '@ui/form-field/form-field.component';
import { VxInputComponent } from '@ui/input/input.component';
import { WebAuthNService } from 'src/app/core/components/kratos-form/webauthn.service';
import {
  csrfToken,
  flowInput,
  flowMessages,
  flowValue,
  hiddenValues,
  offersInput,
  submitFlow,
} from 'src/app/services/authentication/kratos-flow';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';
import { AuthHeaderComponent } from '../../components/auth-header/auth-header.component';
import { AuthPanelComponent } from '../../components/auth-panel/auth-panel.component';
import { CodeEntryComponent } from 'src/app/core/components/code-entry/code-entry.component';
import { codeStep } from 'src/app/core/components/code-entry/code-step';
import { KratosMessagesComponent } from 'src/app/core/components/kratos-messages/kratos-messages.component';

/** How the second factor is given; backup codes are the last resort. */
type Method = 'totp' | 'webauthn' | 'lookup_secret';

type SubmitOutcome = Session | 'rejected' | 'failed';

/**
 * The second sign-in step (aal2): the authenticator app's code, a security
 * key, or one of the backup codes — whichever the account set up.
 */
@Component({
  selector: 'app-two-factor',
  templateUrl: './two-factor.component.html',
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    AuthHeaderComponent,
    AuthPanelComponent,
    CodeEntryComponent,
    KratosMessagesComponent,
    VxButtonComponent,
    VxFormFieldComponent,
    VxInputComponent,
  ],
  providers: [WebAuthNService],
  host: { class: 'flex flex-col' },
})
export class TwoFactorComponent {
  private readonly auth = inject(KratosAuthenticationService);
  private readonly webauthn = inject(WebAuthNService);

  readonly flow = input.required<LoginFlow>();

  readonly signedIn = output<Session>();

  /** The flow as Kratos last rendered it (a wrong code re-renders it). */
  private readonly current = linkedSignal(() => this.flow());

  protected readonly offersTotp = computed(() =>
    offersInput(this.current(), 'totp_code', 'totp')
  );

  protected readonly offersBackupCode = computed(() =>
    offersInput(this.current(), 'lookup_secret', 'lookup_secret')
  );

  protected readonly offersSecurityKey = computed(
    () => !!flowInput(this.current(), 'webauthn_login_trigger', 'webauthn')
  );

  /**
   * What the step asks for first: the authenticator app, else the security
   * key, else a backup code. Backup codes are for a lost device, so they
   * are only the main view when the account has nothing else.
   */
  protected readonly method = linkedSignal<Method>(() => {
    if (this.offersTotp()) {
      return 'totp';
    }
    return this.offersSecurityKey() ? 'webauthn' : 'lookup_secret';
  });

  protected readonly messages = computed(() =>
    flowMessages(this.current()).filter(message => message.type === 'error')
  );

  protected readonly step = codeStep();

  /** A submit failed without a flow to explain it; a fresh one may help. */
  protected readonly failed = signal(false);

  protected readonly backupForm = new FormGroup({
    code: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  protected async submitTotp(code: string): Promise<void> {
    const outcome = await this.step.verify(() =>
      this.submit({
        method: 'totp',
        totp_code: code,
        csrf_token: csrfToken(this.current()),
      })
    );
    if (outcome === 'rejected') {
      this.step.reject();
    } else {
      this.finish(outcome);
    }
  }

  protected async submitBackupCode(): Promise<void> {
    const { code } = this.backupForm.controls;
    if (code.invalid) {
      code.markAsTouched();
      return;
    }
    this.finish(
      await this.step.run(() =>
        this.submit({
          method: 'lookup_secret',
          lookup_secret: code.value.trim(),
          csrf_token: csrfToken(this.current()),
        })
      )
    );
  }

  protected async useSecurityKey(): Promise<void> {
    const flow = this.current();
    const trigger = flowInput(flow, 'webauthn_login_trigger', 'webauthn');
    if (!trigger) {
      return;
    }
    const outcome = await this.step.run(async () => {
      let assertion: unknown;
      try {
        assertion = await this.webauthn.webauthnLogin(trigger);
      } catch {
        // cancelled in the browser's dialog, or no key at hand
        return undefined;
      }
      // Kratos keeps the account's `identifier` in the default group
      return this.submit({
        ...hiddenValues(flow, 'default'),
        ...hiddenValues(flow, 'webauthn'),
        identifier: flowValue(flow, 'identifier'),
        method: 'webauthn',
        webauthn_login: JSON.stringify(assertion),
        csrf_token: csrfToken(flow),
      } as UpdateLoginFlowBody);
    });
    this.finish(outcome);
  }

  protected switchMethod(method: Method): void {
    this.method.set(method);
    this.step.reset();
    // a fresh field, not one that says "required" before anything was typed
    this.backupForm.reset();
  }

  /** A fresh 2FA flow after one that failed or expired; the session stays. */
  protected restart(): void {
    this.step.run(async () => {
      try {
        this.current.set(await this.auth.createLoginFlow('aal2'));
        this.failed.set(false);
        this.step.reset();
      } catch {
        // still unreachable: "failed" stays up with its retry
      }
    });
  }

  /** Signs in on a session; a rejected attempt shows Kratos's message. */
  private finish(outcome: SubmitOutcome | undefined): void {
    if (outcome && typeof outcome === 'object') {
      this.signedIn.emit(outcome);
    }
  }

  /**
   * The session; `rejected` when Kratos re-rendered the flow (a wrong
   * code: its messages say so); `failed` when there is no flow to explain
   * it (network, expired flow).
   */
  private async submit(body: UpdateLoginFlowBody): Promise<SubmitOutcome> {
    this.failed.set(false);
    const result = await submitFlow<LoginFlow, Session>(() =>
      this.auth.completeLoginFlowV2(this.current(), body.method, body)
    );
    if (result.ok) {
      return result.value;
    }
    if (result.flow) {
      this.current.set(result.flow);
      return 'rejected';
    }
    this.failed.set(true);
    return 'failed';
  }
}
